import React, { useState } from 'react';
import { Modal, Button, Badge, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';

const MeliCategoryLookup = ({
    show,
    onHide,
    selectedProduct,
    categoryLookupResult,
    categoryLookupLoading,
    categoryLookupError,
    categoryName,
    onViewAttributes,
    onViewFullResponse
}) => {
    // State for reassignment modal
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [reassignLoading, setReassignLoading] = useState(false);
    const [reassignError, setReassignError] = useState(null);
    const [reassignResult, setReassignResult] = useState(null);

    // Form state
    const [instructions, setInstructions] = useState('');
    const [desiredCategoryId, setDesiredCategoryId] = useState('');

    // API endpoint
    const API_ENDPOINT = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/category-reassign';

    const handleReassignCategory = async () => {
        if (!instructions.trim() || !desiredCategoryId.trim()) {
            setReassignError('Please fill in both fields');
            return;
        }

        setReassignLoading(true);
        setReassignError(null);
        setReassignResult(null);

        try {
            const requestBody = {
                product_ids: [selectedProduct.id],
                desired_meli_category_id: desiredCategoryId.trim(),
                desired_description_instructions: instructions.trim()
            };

            console.log('Sending reassignment request:', requestBody);

            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();

            if (response.ok) {
                setReassignResult(result);
                console.log('Reassignment successful:', result);
            } else {
                setReassignError(`API Error: ${result.error || 'Unknown error'}`);
                console.error('Reassignment failed:', result);
            }
        } catch (error) {
            setReassignError(`Network Error: ${error.message}`);
            console.error('Reassignment error:', error);
        } finally {
            setReassignLoading(false);
        }
    };

    const handleCloseReassignModal = () => {
        setShowReassignModal(false);
        setInstructions('');
        setDesiredCategoryId('');
        setReassignError(null);
        setReassignResult(null);
    };

    const handleOpenReassignModal = () => {
        setShowReassignModal(true);
        // Pre-fill with current category ID if available
        if (categoryLookupResult?.product_info?.current_meli_category_id) {
            setDesiredCategoryId(categoryLookupResult.product_info.current_meli_category_id);
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                size="lg"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header
                    closeButton
                    onHide={onHide}
                >
                    <Modal.Title>
                        {selectedProduct ? (
                            <>
                                🔍 MELI Category Lookup
                                {categoryName && (
                                    <div className="mt-1">
                                        <small className="text-muted">
                                            Product: {selectedProduct.sku || selectedProduct.id} | Category: {categoryName}
                                        </small>
                                    </div>
                                )}
                            </>
                        ) : (
                            'MELI Category Lookup'
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {categoryLookupLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Looking up MELI category...</p>
                        </div>
                    )}
                    {categoryLookupResult && (
                        <div>
                            {/* Product Information */}
                            <div className="mb-4 p-3 bg-light rounded">
                                <h6>Product Information</h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Product ID:</strong> {selectedProduct.id || 'N/A'}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Category:</strong> {categoryLookupResult.product_info?.category || 'N/A'}
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <strong>Title:</strong> {categoryLookupResult.product_info?.title || 'N/A'}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>MELI Category ID:</strong> {categoryLookupResult.product_info?.current_meli_category_id || 'None'}
                                    </div>
                                </div>
                            </div>

                            {/* Top Suggestion */}
                            {categoryLookupResult.suggested_category && (
                                <div className="mb-4 p-3 bg-success bg-opacity-10 rounded border border-success">
                                    <h6 className="text-success">🏆 Top Suggestion</h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <strong>Category ID:</strong><br />
                                            <Badge bg="success" className="mt-1">{categoryLookupResult.suggested_category.category_id}</Badge>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Category Name:</strong><br />
                                            <Badge bg="primary" className="mt-1">{categoryLookupResult.suggested_category.category_name}</Badge>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Domain:</strong><br />
                                            <Badge bg="info" className="mt-1">{categoryLookupResult.product_info?.current_meli_category_id || 'None'}</Badge>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* All Suggestions */}
                            {categoryLookupResult.meli_suggestions && categoryLookupResult.meli_suggestions.length > 0 && (
                                <div className="mb-4">
                                    <h6 className="mb-3">🎯 All MELI Suggestions ({categoryLookupResult.meli_suggestions.length} found)</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped table-hover">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th width="10%">#</th>
                                                    <th width="25%">Category Name</th>
                                                    <th width="20%">Category ID</th>
                                                    <th width="20%">Domain</th>
                                                    <th width="25%">Attributes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categoryLookupResult.meli_suggestions.map((suggestion, index) => (
                                                    <tr key={index} className={index === 0 ? 'table-success fw-bold' : ''}>
                                                        <td className="text-center">
                                                            {index === 0 ? (
                                                                <span className="fs-5">🏆</span>
                                                            ) : (
                                                                <Badge bg="secondary">{index + 1}</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <strong>{suggestion.category_name}</strong>
                                                        </td>
                                                        <td>
                                                            <code className="bg-light p-1 rounded">{suggestion.category_id}</code>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary">{suggestion.domain_name}</Badge>
                                                        </td>
                                                        <td>
                                                            {suggestion.attributes && suggestion.attributes.length > 0 ? (
                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    onClick={() => onViewAttributes(suggestion)}
                                                                    className="mt-1"
                                                                >
                                                                    View Attributes
                                                                    <span className="badge bg-info ms-1">{suggestion.attributes.length}</span>
                                                                </Button>
                                                            ) : (
                                                                <span className="text-muted fst-italic">No attributes</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Full Response Toggle */}
                            {categoryLookupResult.meli_full_response && (
                                <div className="mb-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={onViewFullResponse}
                                    >
                                        🔍 View Full API Response
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                    {categoryLookupError && (
                        <Alert variant="danger">
                            <strong>Error looking up MELI category:</strong> {categoryLookupError}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={handleOpenReassignModal}
                        disabled={!categoryLookupResult}
                    >
                        🔄 Reassign Category
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reassignment Modal */}
            <Modal
                show={showReassignModal}
                onHide={handleCloseReassignModal}
                size="smd"
                centered
                backdrop="static"
                className="reassignment-modal"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
            >
                <Modal.Header closeButton onHide={handleCloseReassignModal}>
                    <Modal.Title>🔄 Instructions to Recategorize</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <strong>Instructions to Recategorize</strong>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="e.g. These products are wristwatches"
                                        value={instructions}
                                        onChange={(e) => setInstructions(e.target.value)}
                                        disabled={reassignLoading}
                                    />
                                    <Form.Text className="text-muted">
                                        AI instructions to understand how to recategorize this product.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <strong>MELI Category ID</strong>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. CBT1442"
                                        value={desiredCategoryId}
                                        onChange={(e) => setDesiredCategoryId(e.target.value)}
                                        disabled={reassignLoading}
                                    />
                                    <Form.Text className="text-muted">
                                        Ie. CBT1442
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Loading State */}
                        {reassignLoading && (
                            <div className="text-center py-3">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Processing category reassignment...</p>
                            </div>
                        )}

                        {/* Error Display */}
                        {reassignError && (
                            <Alert variant="danger" className="mt-3">
                                <strong>Error:</strong> {reassignError}
                            </Alert>
                        )}

                        {/* Success Results */}
                        {reassignResult && (
                            <Alert variant="success" className="mt-3">
                                <h6>✅ Reassignment Results</h6>
                                <p><strong>Message:</strong> {reassignResult.message}</p>
                                <p><strong>Summary:</strong></p>
                                <ul className="mb-0">
                                    <li>Total Processed: {reassignResult.summary?.total_processed}</li>
                                    <li>Successful: {reassignResult.summary?.successful}</li>
                                    <li>Failed: {reassignResult.summary?.failed}</li>
                                </ul>

                                {reassignResult.results && reassignResult.results.length > 0 && (
                                    <div className="mt-3">
                                        <h6>Product Results:</h6>
                                        {reassignResult.results.map((result, index) => (
                                            <div key={index} className={`p-2 rounded mb-2 ${result.success ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                                                <strong>Product ID {result.product_id}:</strong> {result.success ? result.message : result.error}
                                                {result.additional_description && (
                                                    <div className="mt-1">
                                                        <small className="text-muted">
                                                            <strong>Additional Description:</strong> {result.additional_description}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={handleReassignCategory}
                        disabled={reassignLoading || !instructions.trim() || !desiredCategoryId.trim()}
                    >
                        {reassignLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            '🔄 Recategorize'
                        )}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleCloseReassignModal}
                        disabled={reassignLoading}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MeliCategoryLookup;
